import React from 'react';
import { Box, Button, CircularProgress, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { alpha, lighten } from '@mui/material/styles';
import { colors, typography } from '../../styles/theme';
import { formatMetaUpdatedAt } from '../metaAdsConnection';
import { formatMetaCountdown } from '../metaAdsConfig';
import { metaAdsIconButtonSx, metaAdsPanelHeaderIconSx } from './metaAdsUi.styles';

const TONE_COLORS = {
  info: colors.primary,
  success: lighten(colors.success, 0.4),
  warning: colors.warning,
  danger: colors.error,
};

const textSx = { fontFamily: typography.fontFamily.primary, lineHeight: 1.4 };

const PAUSED_FALLBACK = {
  stalled: 'La carga no avanzó en los últimos intentos.',
  long_wait: 'Meta pidió esperar antes de seguir.',
  max_runs: 'Pausamos la carga automática.',
};

/**
 * Estado visible del banner a partir de `sync_progress`. Puro (testeable).
 * `paused` (de useMetaSync): la continuación sola se cortó (estancada, espera
 * larga o tope); sin spinner salvo que haya un pedido o `sync_running`.
 */
export function metaSyncBannerModel(progress, {
  pending = false, pendingMessage = null, paused = null, now = new Date(),
} = {}) {
  if (!progress) {
    // API sin `sync_progress` (o todavía sin respuesta): solo el spinner del pedido.
    return pending
      ? { kind: 'busy', tone: 'info', spinner: true, message: pendingMessage || 'Actualizando datos de Meta…', detail: null, bar: null, note: null }
      : null;
  }
  const loading = progress.state === 'initial_load' || progress.state === 'updating';
  const working = pending || progress.syncRunning || (loading && !paused);
  if (progress.state === 'up_to_date') {
    // `detail` ya viene armado por la API («Actualizado hoy a las HH:MM», hora de
    // Córdoba). Nuestro formato solo si falta; sin ninguno de los dos, no hay línea.
    // Refrescando (sync sola al abrir o el ícono): misma línea, no bloquea, con
    // spinner chico y «Actualizando…».
    const detail = progress.detail || formatMetaUpdatedAt(progress.lastSyncAt, now);
    const refreshing = Boolean(pending || progress.syncRunning);
    if (!detail && !refreshing) return null;
    return {
      kind: 'done', tone: 'success', spinner: refreshing, refreshing, message: 'Datos al día', detail, bar: null, note: null,
    };
  }
  if (!progress.message && !working && !paused) return null;
  const bar = progress.showProgressBar && progress.pct != null
    ? { pct: progress.pct, daysLoaded: progress.daysLoaded, daysTotal: progress.daysTotal }
    : null;
  let note = null;
  if (paused && loading) {
    const msg = paused.message && paused.message !== progress.message ? paused.message : null;
    note = msg || PAUSED_FALLBACK[paused.reason] || PAUSED_FALLBACK.max_runs;
  }
  return {
    kind: progress.state || 'unknown',
    tone: progress.tone,
    spinner: Boolean(working),
    message: progress.message || 'Actualizando datos de Meta…',
    // `error` también trae la hora en `detail`: tal cual.
    detail: progress.detail,
    bar,
    note,
  };
}

/**
 * Ícono «Actualizar datos» (dueño con `can_sync`): spinner mientras corre y
 * deshabilitado con la cuenta regresiva en el tooltip mientras `resumeInS` > 0.
 * Lo usa el banner con «Datos al día» y el header fijo de Campañas.
 */
export function MetaSyncRefreshButton({ refreshing = false, onRefresh, resumeInS = 0, inPanelHeader = false }) {
  const waiting = resumeInS > 0;
  const tooltip = waiting ? `Actualizar datos · disponible en ${formatMetaCountdown(resumeInS)}` : 'Actualizar datos';
  return (
    <Tooltip title={tooltip} placement={inPanelHeader ? 'bottom' : 'top'} arrow>
      {/* span: el tooltip también aparece con el botón deshabilitado. */}
      <span>
        <IconButton
          size="small"
          aria-label="Actualizar datos"
          onClick={onRefresh}
          disabled={refreshing || waiting}
          data-testid="meta-ads-sync-refresh"
          sx={inPanelHeader ? metaAdsPanelHeaderIconSx : metaAdsIconButtonSx}
        >
          {refreshing
            ? <CircularProgress size={inPanelHeader ? 16 : 14} thickness={5} sx={{ color: 'inherit' }} data-testid="meta-ads-sync-spinner" aria-label="Cargando" />
            : <RefreshIcon sx={{ fontSize: inPanelHeader ? 20 : 16 }} />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

/**
 * Banner único de la carga de Meta (Ajuste 7): reemplaza «Estamos trayendo los
 * últimos 90 días / Falta la primera carga» y «Actualización parcial…». Texto de
 * la API tal cual (`message` / `detail`), color por `tone`, spinner mientras
 * corre y barra con `pct` si `show_progress_bar`. La continuación es automática
 * (useMetaSync); si se pausa (`paused`), muestra el motivo y, solo para el dueño
 * con `can_sync` (`onResume`), «Seguir cargando» — deshabilitado mientras
 * `resumeInS` > 0 (`retry_after_s` / `next_sync_allowed_at`). El editor solo mira.
 *
 *
 * Con «Datos al día», `onRefresh` (dueño con `can_sync`) agrega el ícono
 * «Actualizar datos»: spinner mientras corre y deshabilitado con la cuenta
 * regresiva en el tooltip mientras `resumeInS` > 0 (`next_sync_allowed_at`).
 *
 * `hideUpToDate` (Campañas): la línea «Datos al día» no se muestra (la frescura y el
 * ícono viven en el header fijo); carga, pausa y error sí.
 *
 * @param {{ progress: object|null, pending?: boolean, pendingMessage?: string|null, paused?: object|null,
 *   onResume?: (() => void)|null, onRefresh?: (() => void)|null, resumeInS?: number, now?: Date,
 *   hideUpToDate?: boolean }} props
 */
export default function MetaSyncBanner({
  progress, pending = false, pendingMessage = null, paused = null, onResume = null, onRefresh = null, resumeInS = 0, now,
  hideUpToDate = false,
}) {
  const model = metaSyncBannerModel(progress, { pending, pendingMessage, paused, now });
  if (!model) return null;
  if (hideUpToDate && model.kind === 'done') return null;
  const color = TONE_COLORS[model.tone] || TONE_COLORS.info;

  if (model.kind === 'done') {
    return (
      <Box
        data-testid="meta-ads-sync-banner"
        data-state="up_to_date"
        role={model.refreshing ? 'status' : undefined}
        aria-live={model.refreshing ? 'polite' : undefined}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 24 }}
      >
        <Typography sx={{ ...textSx, fontSize: '12px', fontWeight: 600, color }}>
          {[model.message, model.detail, model.refreshing ? 'Actualizando…' : null].filter(Boolean).join(' · ')}
        </Typography>
        {onRefresh ? (
          <MetaSyncRefreshButton refreshing={model.refreshing} onRefresh={onRefresh} resumeInS={resumeInS} />
        ) : model.refreshing ? (
          <CircularProgress size={12} thickness={5} sx={{ color, ml: 0.5 }} data-testid="meta-ads-sync-spinner" aria-label="Cargando" />
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="meta-ads-sync-banner"
      data-state={model.kind}
      sx={{
        display: 'flex', flexDirection: 'column', gap: 0.75, px: 1.25, py: 1, borderRadius: '8px',
        bgcolor: alpha(color, 0.12), border: `1px solid ${alpha(color, 0.28)}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        {model.spinner ? (
          <CircularProgress size={14} thickness={5} sx={{ color, mt: '2px', flexShrink: 0 }} data-testid="meta-ads-sync-spinner" aria-label="Cargando" />
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ ...textSx, fontSize: '13px', fontWeight: 600, color: colors.textPanelTitle }}>{model.message}</Typography>
          {model.detail ? (
            <Typography sx={{ ...textSx, mt: 0.25, fontSize: '12px', color: colors.textSecondary }}>{model.detail}</Typography>
          ) : null}
        </Box>
      </Box>
      {model.note ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} data-testid="meta-ads-sync-paused">
          <Typography sx={{ ...textSx, fontSize: '12px', color: colors.textSecondary }}>
            {model.note}
            {onResume && resumeInS > 0 ? ` Podés seguir en ${formatMetaCountdown(resumeInS)}.` : ''}
          </Typography>
          {onResume ? (
            <Button
              size="small"
              onClick={onResume}
              disabled={pending || resumeInS > 0}
              sx={{ textTransform: 'none', fontWeight: 600, px: 1, minWidth: 0, ml: 'auto', color: colors.primaryLight }}
            >
              Seguir cargando
            </Button>
          ) : null}
        </Box>
      ) : null}
      {model.bar ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="meta-ads-sync-progress">
          <LinearProgress
            variant="determinate"
            value={model.bar.pct}
            aria-label="Progreso de la carga"
            sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: alpha(color, 0.18), '& .MuiLinearProgress-bar': { bgcolor: color } }}
          />
          <Typography sx={{ ...textSx, fontSize: '12px', color: colors.textSecondary, whiteSpace: 'nowrap' }}>
            {model.bar.daysTotal ? `${model.bar.daysLoaded ?? 0}/${model.bar.daysTotal} días` : `${model.bar.pct}%`}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
